import { Injectable, inject } from '@angular/core';
import { supabase } from './supabase.client';
import { AuthService } from '../auth/auth.service';
import { ErrorLoggerService } from '../error/error-logger.service';
import { Branch } from '../../models';

export interface BranchInput {
  name: string;
  address?: string | null;
  phone?: string | null;
  emergencyPhone?: string | null;
  isActive?: boolean;
}

interface BranchRow {
  id: string;
  practice_id: string;
  name: string;
  address: string | null;
  phone: string | null;
  emergency_phone: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

const mapRow = (r: BranchRow): Branch => ({
  id: r.id,
  practiceId: r.practice_id,
  name: r.name,
  address: r.address,
  phone: r.phone,
  emergencyPhone: r.emergency_phone,
  isActive: r.is_active,
  isDefault: r.is_default,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  deletedAt: r.deleted_at,
});

const SELECT_COLS = 'id, practice_id, name, address, phone, emergency_phone, is_active, is_default, created_at, updated_at, deleted_at';

@Injectable({ providedIn: 'root' })
export class BranchService {
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly auth = inject(AuthService);

  async fetchAll(): Promise<Branch[]> {
    const { data, error } = await supabase
      .from('branches')
      .select(SELECT_COLS)
      .is('deleted_at', null)
      .order('name', { ascending: true });

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.fetchAll' });
      throw error;
    }
    return (data as BranchRow[] | null ?? []).map(mapRow);
  }

  async fetchActive(): Promise<Branch[]> {
    const { data, error } = await supabase
      .from('branches')
      .select(SELECT_COLS)
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.fetchActive' });
      throw error;
    }
    return (data as BranchRow[] | null ?? []).map(mapRow);
  }

  async fetchById(id: string): Promise<Branch | null> {
    const { data, error } = await supabase
      .from('branches')
      .select(SELECT_COLS)
      .eq('id', id)
      .maybeSingle();

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.fetchById' });
      throw error;
    }
    return data ? mapRow(data as BranchRow) : null;
  }

  async create(input: BranchInput): Promise<Branch> {
    const practiceId = this.auth.practiceId();
    if (!practiceId) throw new Error('No active practice selected');

    const { data, error } = await supabase
      .from('branches')
      .insert({
        practice_id: practiceId,
        name: input.name,
        address: input.address ?? null,
        phone: input.phone ?? null,
        emergency_phone: input.emergencyPhone ?? null,
        is_active: input.isActive ?? true,
      })
      .select(SELECT_COLS)
      .single();

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.create' });
      throw error;
    }
    return mapRow(data as BranchRow);
  }

  async update(id: string, partial: Partial<BranchInput>): Promise<void> {
    const payload: Record<string, unknown> = {};
    if (partial.name !== undefined) payload['name'] = partial.name;
    if (partial.address !== undefined) payload['address'] = partial.address;
    if (partial.phone !== undefined) payload['phone'] = partial.phone;
    if (partial.emergencyPhone !== undefined) payload['emergency_phone'] = partial.emergencyPhone;
    if (partial.isActive !== undefined) payload['is_active'] = partial.isActive;

    const { error } = await supabase.from('branches').update(payload).eq('id', id);

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.update' });
      throw error;
    }
  }

  async softDelete(id: string): Promise<void> {
    const { error } = await supabase
      .from('branches')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.softDelete' });
      throw error;
    }
  }

  async setDefault(id: string): Promise<void> {
    const { error } = await supabase.rpc('set_default_branch', { p_branch_id: id });
    if (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchService.setDefault' });
      throw error;
    }
  }
}
